import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SyncIcon from '@mui/icons-material/Sync';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBilling } from '../context/BillingContext';
import { OfflineBanner } from '../components/OfflineBanner';
import { WriteBlockedBanner } from '../components/WriteBlockedBanner';
import { useSnackbar } from '../context/SnackbarContext';
import { useOutbox } from '../outbox/outboxStore';
import { OutboxWorkerBoot } from '../outbox/OutboxWorkerBoot';
import { PushRegistrationBoot } from '../components/PushRegistrationBoot';
import { NotificationBell } from '../components/notifications/NotificationBell';
import { ClockWidget } from '../components/attendance/ClockWidget';
import { GlobalSearchModal } from '../components/shell/GlobalSearchModal';
import { KeyboardShortcutsModal } from '../components/shell/KeyboardShortcutsModal';
import { ShellShortcutsProvider } from '../context/ShellShortcutsContext';
import { useAppKeyboardShortcuts } from '../hooks/useAppKeyboardShortcuts';
import { useCanManage } from '../hooks/useCanManage';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { isNative } from '../platform/isNative';
import { AppPreferences } from '../platform/preferences';
import { pushApi } from '../api/push';
import { clearDeliveredNotifications } from '../platform/push';
import { AppNavDrawer } from './AppNavDrawer';

const DRAWER_WIDTH = 240;

const ROLE_LABELS: Record<string, string> = {
  AMMINISTRATORE_AZIENDA: 'Amministratore',
  RESPONSABILE: 'Responsabile',
  OPERATORE: 'Operatore',
};

export function AppShellPage() {
  const { user, logout } = useAuth();
  const { isWriteBlocked } = useBilling();
  const canManage = useCanManage();
  const { hasPresence } = useFeatureFlags();
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const shellShortcuts = useMemo(
    () => ({
      openGlobalSearch: () => setGlobalSearchOpen(true),
      openShortcutsHelp: () => setShortcutsOpen(true),
    }),
    [],
  );

  useAppKeyboardShortcuts({
    openGlobalSearch: shellShortcuts.openGlobalSearch,
    openShortcutsHelp: shellShortcuts.openShortcutsHelp,
    canCreateWorksite: canManage,
  });

  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const { pendingCount } = useOutbox();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  const closeMobileDrawer = useCallback((opts?: { fromLink?: boolean }) => {
    setMobileOpen(false);
    if (!opts?.fromLink) {
      setTimeout(() => menuButtonRef.current?.focus({ preventScroll: true }), 0);
    }
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    setAnchorEl(null);
    try {
      if (isNative()) {
        try {
          const deviceId = await AppPreferences.get('cantir.push.deviceId');
          if (deviceId) {
            await pushApi.unregister(deviceId);
          }
          await clearDeliveredNotifications();
        } catch {
          // Push unregistration failure is non-fatal — proceed with logout
        }
      }
      await logout();
      navigate('/login', { replace: true });
    } catch {
      showMessage('Errore durante il logout.', 'error');
    }
  };

  return (
    <ShellShortcutsProvider value={shellShortcuts}>
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <OutboxWorkerBoot />
      <PushRegistrationBoot />

      <AppBar
        position="fixed"
        elevation={1}
        sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}
      >
        <Toolbar
          disableGutters
          sx={{
            px: { xs: 1, sm: 2 },
            gap: { xs: 0.25, sm: 1 },
            minWidth: 0,
          }}
        >
          <IconButton
            ref={menuButtonRef}
            color="inherit"
            edge="start"
            size="small"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label="Apri menu"
            sx={{ mr: { xs: 0.25, sm: 1 }, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            Cantir
          </Typography>

          <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
            <Tooltip title="Ricerca globale (Ctrl+K)">
              <IconButton
                color="inherit"
                size="small"
                onClick={() => setGlobalSearchOpen(true)}
                aria-label="Ricerca globale"
              >
                <SearchIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Scorciatoie tastiera">
              <IconButton
                color="inherit"
                size="small"
                onClick={() => setShortcutsOpen(true)}
                aria-label="Scorciatoie tastiera"
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>

            <Chip
              label={ROLE_LABELS[user?.role ?? ''] ?? user?.role}
              size="small"
              sx={{
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                display: { xs: 'none', sm: 'inline-flex' },
                maxWidth: { sm: 140, md: 'none' },
                '& .MuiChip-label': { px: 1, overflow: 'hidden', textOverflow: 'ellipsis' },
              }}
              variant="outlined"
            />

            <Tooltip title={pendingCount > 0 ? `${pendingCount} operazioni in coda` : 'Sincronizzazione'}>
              <IconButton
                color="inherit"
                size="small"
                onClick={() => navigate('/app/outbox')}
                aria-label="coda sincronizzazione"
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                <Badge
                  badgeContent={pendingCount > 0 ? pendingCount : undefined}
                  color="warning"
                  max={99}
                >
                  <SyncIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <NotificationBell />

            <IconButton
              color="inherit"
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              aria-label="Menu account"
            >
              <Avatar sx={{ width: 28, height: 28, bgcolor: 'secondary.main', fontSize: 12 }}>
                {user?.userId?.slice(0, 2).toUpperCase() ?? 'CA'}
              </Avatar>
            </IconButton>
          </Stack>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            <MenuItem dense disabled>
              <Typography variant="caption" color="text.secondary">
                {ROLE_LABELS[user?.role ?? ''] ?? user?.role}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate('/app/profile');
              }}
            >
              <PersonIcon fontSize="small" sx={{ mr: 1 }} />
              Profilo
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setShortcutsOpen(true);
              }}
              sx={{ display: { xs: 'flex', sm: 'none' } }}
            >
              <HelpOutlineIcon fontSize="small" sx={{ mr: 1 }} />
              Scorciatoie tastiera
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate('/app/outbox');
              }}
              sx={{ display: { xs: 'flex', sm: 'none' } }}
            >
              <SyncIcon fontSize="small" sx={{ mr: 1 }} />
              {pendingCount > 0 ? `Coda sincronizzazione (${pendingCount})` : 'Coda sincronizzazione'}
            </MenuItem>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate('/app/settings/notifications');
              }}
            >
              <NotificationsActiveIcon fontSize="small" sx={{ mr: 1 }} />
              Impostazioni notifiche
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={closeMobileDrawer}
        ModalProps={{ keepMounted: false }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
      >
        <AppNavDrawer />
      </Drawer>

      {/* Desktop permanent drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box' },
        }}
        open
      >
        <AppNavDrawer />
      </Drawer>

      {/* Page content — minWidth:0 evita overflow orizzontale da flex (contenuto lungo / tab / tabelle) */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          maxWidth: '100%',
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
          mt: '64px',
          minHeight: 'calc(100dvh - 64px)',
          bgcolor: 'background.default',
          overflowX: 'hidden',
        }}
      >
        <OfflineBanner />
        <WriteBlockedBanner visible={isWriteBlocked} />
        {location.pathname === '/app/reports' && (
          <Box sx={{ maxWidth: 'md', mx: 'auto', py: 3, pb: 0, px: { xs: 2, sm: 3 } }}>
            <Typography variant="h5" fontWeight={700} mb={2}>
              Rapportini
            </Typography>
          </Box>
        )}
        <Outlet />
        {hasPresence && <ClockWidget />}
      </Box>

      <GlobalSearchModal open={globalSearchOpen} onClose={() => setGlobalSearchOpen(false)} />
      <KeyboardShortcutsModal open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </Box>
    </ShellShortcutsProvider>
  );
}
